import { collection, getDocs, query, where } from 'firebase/firestore';
import type { ProjectFileStream, SalesOrderLineItem } from '../types';
import { db } from './firebase';
import {
  buildBillablePackages,
  structuresFromLineItems,
  WHOLE_PROJECT_PACKAGE_KEY,
  type BillablePackage,
  type ContractStructure,
} from './partialInvoiceAllocation';
import { groupStreamsByStructure } from './projectFileStreamGroups';

/**
 * Structure groups from the project's Files rail. The picker bills those
 * groups, not each proposal structure. The empty "Whole project" bucket is
 * not billable.
 */
export async function loadBillablePackagesForDeal(
  dealId: string,
  lineItems: SalesOrderLineItem[] | undefined,
  projectId?: string,
): Promise<{ structures: ContractStructure[]; packages: BillablePackage[] }> {
  const structures = structuresFromLineItems(lineItems || []);
  let resolvedProjectId = projectId;
  if (!resolvedProjectId && dealId) {
    const projects = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));
    resolvedProjectId = projects.docs[0]?.id;
  }
  const groups: Array<{ key: string; label: string; structureIds: string[] }> = [];
  if (resolvedProjectId) {
    const streams = await getDocs(
      query(collection(db, 'projectFileStreams'), where('projectId', '==', resolvedProjectId)),
    );
    const parsed = streams.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as ProjectFileStream));
    for (const group of groupStreamsByStructure(
      parsed,
      structures.map((structure) => ({ id: structure.structureId, name: structure.structureName })),
    )) {
      if (group.structureId === WHOLE_PROJECT_PACKAGE_KEY || group.structureIds.length === 0) continue;
      groups.push({
        key: group.structureId,
        label: group.customLabel || group.structureName,
        structureIds: group.structureIds,
      });
    }
  }
  return { structures, packages: buildBillablePackages(groups, structures) };
}
